import React, { useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiX } from 'react-icons/fi';
import { ACCENT_CLASSES, AccentColor } from '../../types/work';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  accentColor?: AccentColor;
  children: React.ReactNode;
}

const SlideInPanel: React.FC<Props> = ({ open, onClose, title, subtitle, accentColor = 'gray', children }) => {
  const { darkMode } = useTheme();

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  if (!open) return null;

  const borderClass = ACCENT_CLASSES[accentColor].border;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-end z-50" onClick={onClose}>
      <div
        className={`w-full max-w-3xl max-h-[80vh] mt-[10vh] overflow-y-auto rounded-l-xl ${darkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl animate-slide-in-right`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 p-5 border-b border-l-4 ${darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'} ${borderClass}`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
              {subtitle && <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</p>}
            </div>
            <button onClick={onClose} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Content */}
        <div className="p-5">
          {children}
        </div>
      </div>
    </div>
  );
};

export default SlideInPanel;
